/**
 * pwaInstall — V1.9.11
 *
 * Chrome fires `beforeinstallprompt` ONCE per page load, very early in the
 * lifecycle. If no React component has registered a listener by then, the
 * event is lost and there's no way to prompt the user later. This module
 * captures the event at boot (before any React tree mounts), stashes it,
 * and exposes a small subscribe API + a manual `promptInstall()` function
 * that any component can call.
 *
 * Usage:
 *   import { promptInstall, useInstallState } from "@/lib/pwaInstall";
 *   const { canInstall, isIOS, isStandalone } = useInstallState();
 *   <button onClick={promptInstall} disabled={!canInstall && !isIOS}>Install</button>
 */

import { useEffect, useState } from "react";

let deferredPrompt = null;
let installed = isStandaloneNow();
const listeners = new Set();

function isStandaloneNow() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.navigator?.standalone === true
    );
}

export function isIOSDevice() {
    if (typeof window === "undefined") return false;
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function isAndroidDevice() {
    if (typeof window === "undefined") return false;
    return /android/i.test(window.navigator.userAgent);
}

export function isMobileDevice() {
    return isIOSDevice() || isAndroidDevice();
}

function notify() {
    listeners.forEach((cb) => {
        try {
            cb({
                canInstall: !!deferredPrompt,
                isIOS: isIOSDevice(),
                isAndroid: isAndroidDevice(),
                isMobile: isMobileDevice(),
                isStandalone: installed,
            });
        } catch {
            /* ignore */
        }
    });
}

let booted = false;
export function bootPwaInstallCapture() {
    if (booted || typeof window === "undefined") return;
    booted = true;
    window.addEventListener("beforeinstallprompt", (e) => {
        e.preventDefault();
        deferredPrompt = e;
        notify();
    });
    window.addEventListener("appinstalled", () => {
        deferredPrompt = null;
        installed = true;
        notify();
    });
    window
        .matchMedia?.("(display-mode: standalone)")
        ?.addEventListener?.("change", (ev) => {
            installed = ev.matches;
            notify();
        });
}

export async function promptInstall() {
    if (deferredPrompt) {
        try {
            deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;
            deferredPrompt = null;
            notify();
            return choice?.outcome === "accepted";
        } catch {
            return false;
        }
    }
    return false;
}

export function useInstallState() {
    const [state, setState] = useState({
        canInstall: !!deferredPrompt,
        isIOS: isIOSDevice(),
        isAndroid: isAndroidDevice(),
        isMobile: isMobileDevice(),
        isStandalone: installed,
    });
    useEffect(() => {
        listeners.add(setState);
        setState({
            canInstall: !!deferredPrompt,
            isIOS: isIOSDevice(),
            isAndroid: isAndroidDevice(),
            isMobile: isMobileDevice(),
            isStandalone: installed,
        });
        return () => {
            listeners.delete(setState);
        };
    }, []);
    return state;
}
